import { observer } from 'mobx-react-lite';

import { Columns } from 'components/base/Columns';
import { EditNumber } from 'components/forms/EditNumber';
import { EditSelect, type EditSelectOptionList } from 'components/forms/EditSelect';
import { EditString } from 'components/forms/EditString';
import { Field } from 'components/forms/Field';
import { Segmented, type SegmentedOption } from 'components/forms/Segmented';
import { Switch } from 'components/forms/Switch';
import type { OutputMatcherFile, OutputMatcherMatchType, OutputMatcherProceed } from 'api/node_definitions/endpoints/types';

import type { EditableOutputMatcher, OverrideStatus } from './EditableOutputMatcher';
import { ReturnCodeChips } from './ReturnCodeChips';

const OUTPUT_FILE_OPTIONS: EditSelectOptionList<OutputMatcherFile> = [
  { key: 'stdout.log', value: 'stdout.log', label: 'stdout.log' },
  { key: 'stderr.log', value: 'stderr.log', label: 'stderr.log' },
];

const MATCH_TYPE_OPTIONS: readonly SegmentedOption<OutputMatcherMatchType>[] = [
  { value: 'file', label: 'File content' },
  { value: 'return_code', label: 'Return code' },
];

// Fail first — the default; Success forces exit 0. Fail accepts empty or any non-zero integer (not 0).
const OVERRIDE_OPTIONS: readonly SegmentedOption<OverrideStatus>[] = [
  { value: 'fail', label: 'Fail', intent: 'danger' },
  { value: 'success', label: 'Success', intent: 'success' },
];

const PROCEED_OPTIONS: readonly SegmentedOption<OutputMatcherProceed>[] = [
  { value: 'continue', label: 'Continue' },
  { value: 'abort', label: 'Abort' },
];

interface MatcherProps {
  readonly matcher: EditableOutputMatcher;
}

/** File-content match source fields (output file, search string, regex flags). */
const FileMatchFields = observer<MatcherProps>(({ matcher }) => (
  <>
    <Field label="Output file name">
      <EditSelect value={matcher.outputFile} onChange={(v) => (matcher.outputFile = v)} options={OUTPUT_FILE_OPTIONS} />
    </Field>
    <Field label="Search string">
      <EditString value={matcher.searchString} onChange={(v) => (matcher.searchString = v)} />
    </Field>
    <Switch label="Case insensitive" checked={matcher.caseInsensitive} onChange={(v) => (matcher.caseInsensitive = v)} />
    <Switch
      label="Use regex"
      checked={matcher.useRegex}
      onChange={(v) => (matcher.useRegex = v)}
      helpText="Match the search string as a Go RE2 regular expression."
    />
  </>
));

/**
 * Return-code match source field (chips input for exit codes). Only the lead clause varies with emptiness; how to enter a
 * code is left to the input's own placeholder rather than repeated here, which would state it twice at once while empty.
 */
const ReturnCodeMatchFields = observer<MatcherProps>(({ matcher }) => {
  const lead = matcher.returnCodes.length === 0 ? 'Add at least one return code.' : "Matches when the node's exit code is one of these values.";
  return (
    <Field label="Return codes" mandatory helpText={`${lead} Negatives allowed.`}>
      <ReturnCodeChips
        codes={matcher.returnCodes}
        onChange={(v) => (matcher.returnCodes = v)}
        ariaLabel="Return codes"
        emptyPlaceholder="Type a code and press Enter"
      />
    </Field>
  );
});

/** Handle column fields (status override, override return code, proceed). */
const HandleFields = observer<MatcherProps>(({ matcher }) => (
  <>
    <Field label="Override node instance status">
      <Segmented
        value={matcher.overrideStatus}
        onChange={(v) => (matcher.overrideStatus = v)}
        options={OVERRIDE_OPTIONS}
        ariaLabel="Override node instance status"
      />
    </Field>
    <Field
      label="Override return code"
      helpText="Backend mapping: 0 → completed (Success), non-zero → failed, empty → keep the agent's exit code and status. Fail accepts any non-zero integer (including negatives) or empty."
    >
      <EditNumber value={matcher.overrideReturnCode} onChange={(v) => (matcher.overrideReturnCode = v)} disabled={matcher.overrideStatus !== 'fail'} />
    </Field>
    <Field label="Proceed matching">
      <Segmented value={matcher.proceed} onChange={(v) => (matcher.proceed = v)} options={PROCEED_OPTIONS} ariaLabel="Proceed matching" />
    </Field>
  </>
));

/**
 * The fields of a single output matcher, laid out as Match | Handle columns. A pure `observer` view over an
 * `EditableOutputMatcher`: each control reads a getter and writes a bound setter, no local state. Match type switches
 * between file-content fields and a return-codes chips input. Success disables the override return code and shows 0;
 * Fail keeps it editable (empty or any non-zero integer — entering 0 switches to Success).
 */
export const OutputMatcherFields = observer<MatcherProps>(({ matcher }) => (
  <Columns>
    <Columns.Column
      heading="Match"
      subtitle={matcher.matchType === 'return_code' ? 'Which return codes trigger this matcher.' : 'Which file to scan and what to look for.'}
    >
      <Field label="Match type">
        <Segmented value={matcher.matchType} onChange={(v) => (matcher.matchType = v)} options={MATCH_TYPE_OPTIONS} ariaLabel="Match type" />
      </Field>
      {matcher.matchType === 'return_code' ? <ReturnCodeMatchFields matcher={matcher} /> : <FileMatchFields matcher={matcher} />}
    </Columns.Column>

    <Columns.Column heading="Handle" subtitle="What to do on a match.">
      <HandleFields matcher={matcher} />
    </Columns.Column>
  </Columns>
));
